import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, Save as SaveIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import produccionService, { MaquinaDto, MoldeDto } from '../../services/produccionService';
import productoService, { OPTIONS_PRODUCTO_OPERATIVO } from '../../services/productoService';
import ubicacionService, { Ubicacion, etiquetaUbicacionSeleccion } from '../../services/ubicacionService';
import recetaService, { RecetaListItemDto } from '../../services/recetaService';
import RecetaArticuloCodigoField from './RecetaArticuloCodigoField';
import RecetaArticuloSearchDialog, {
  mapProductoToRecetaArticulo,
  RecetaArticuloSeleccionado,
} from './RecetaArticuloSearchDialog';
import RecetaCodigoLookupField from './RecetaCodigoLookupField';
import RecetaSearchDialog from './RecetaSearchDialog';

const OrdenProduccionForm: React.FC = () => {
  const { id } = useParams();
  const isNew = !id || id === 'nuevo';
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [codigoArticulo, setCodigoArticulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [codigoReceta, setCodigoReceta] = useState('');
  const [descripcionReceta, setDescripcionReceta] = useState('');
  const [cantidad, setCantidad] = useState(1);
  const [depositoOrigen, setDepositoOrigen] = useState<number | ''>('');
  const [depositoDestino, setDepositoDestino] = useState<number | ''>('');
  const [codigoMaquina, setCodigoMaquina] = useState<number | ''>('');
  const [codigoMolde, setCodigoMolde] = useState<number | ''>('');
  const [observaciones, setObservaciones] = useState('');
  const [ubicaciones, setUbicaciones] = useState<Ubicacion[]>([]);
  const [maquinas, setMaquinas] = useState<MaquinaDto[]>([]);
  const [moldes, setMoldes] = useState<MoldeDto[]>([]);
  const [saving, setSaving] = useState(false);
  const [resolvingPt, setResolvingPt] = useState(false);
  const [ptError, setPtError] = useState<string | null>(null);
  const [recetaError, setRecetaError] = useState<string | null>(null);
  const [ptDialogOpen, setPtDialogOpen] = useState(false);
  const [recetaDialogOpen, setRecetaDialogOpen] = useState(false);
  const [puedeConfirmar, setPuedeConfirmar] = useState(false);
  const [estadoOrden, setEstadoOrden] = useState<string | null>(null);

  useEffect(() => {
    ubicacionService.getUbicaciones({ pageNumber: 1, pageSize: 100 }).then(setUbicaciones).catch(() => setUbicaciones([]));
    produccionService.getMaquinas().then(setMaquinas).catch(() => setMaquinas([]));
    produccionService.getMoldes().then(setMoldes).catch(() => setMoldes([]));
  }, []);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await produccionService.getOrden(Number(id));
        if (cancelled) return;
        setCodigoArticulo(data.codigoArticulo);
        setDescripcion(data.descripcion ?? '');
        setCodigoReceta(data.codigoReceta ?? '');
        setDescripcionReceta('');
        setCantidad(data.cantidad ?? 1);
        setDepositoOrigen(data.depositoOrigen ?? '');
        setDepositoDestino(data.depositoDestino ?? '');
        setCodigoMaquina(data.codigoMaquina ?? '');
        setCodigoMolde(data.codigoMolde ?? '');
        setObservaciones(data.observaciones ?? '');
        setPuedeConfirmar(Boolean(data.puedeConfirmar));
        setEstadoOrden(data.estado ?? null);
      } catch {
        enqueueSnackbar('No se pudo cargar la orden', { variant: 'error' });
        navigate('/produccion/ordenes');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isNew, enqueueSnackbar, navigate]);

  const aplicarRecetaAsociada = useCallback(
    async (codigoPt: string) => {
      const receta = await recetaService.getPorProducto(codigoPt);
      if (!receta) {
        setCodigoReceta('');
        setDescripcionReceta('');
        setRecetaError('El artículo no tiene receta asociada');
        enqueueSnackbar('El artículo no tiene receta asociada', { variant: 'warning' });
        return;
      }
      setCodigoReceta(receta.codigoReceta);
      setDescripcionReceta(receta.descripcion);
      setRecetaError(null);
      const batchQty = Number(receta.productoTerminado?.cantidad);
      if (batchQty > 0) setCantidad(batchQty);
    },
    [enqueueSnackbar],
  );

  const aplicarProductoTerminado = useCallback(
    async (item: RecetaArticuloSeleccionado) => {
      setPtError(null);
      setCodigoArticulo(item.codigoArticulo);
      setDescripcion(item.descripcion);
      try {
        await aplicarRecetaAsociada(item.codigoArticulo);
      } catch {
        setCodigoReceta('');
        setDescripcionReceta('');
        setRecetaError('No se pudo obtener la receta del artículo');
      }
    },
    [aplicarRecetaAsociada],
  );

  const resolveProductoExact = useCallback(
    async (codigo: string) => {
      const trimmed = codigo.trim();
      if (!trimmed || !isNew) return;
      setResolvingPt(true);
      setPtError(null);
      try {
        const producto = await productoService.getProducto(trimmed, OPTIONS_PRODUCTO_OPERATIVO);
        const tipo = String(producto.tipoArticulo ?? '').trim().toUpperCase();
        if (tipo && tipo !== 'PT' && tipo !== 'SE') {
          setPtError('El artículo debe ser Producto Terminado o Semi Elaborado');
          setDescripcion('');
          setCodigoReceta('');
          setDescripcionReceta('');
          setPtDialogOpen(true);
          return;
        }
        await aplicarProductoTerminado(mapProductoToRecetaArticulo(producto));
      } catch {
        setPtError('No existe en el maestro de artículos');
        setDescripcion('');
        setCodigoReceta('');
        setDescripcionReceta('');
        setPtDialogOpen(true);
      } finally {
        setResolvingPt(false);
      }
    },
    [isNew, aplicarProductoTerminado],
  );

  const aplicarRecetaSeleccionada = useCallback(
    async (item: RecetaListItemDto) => {
      setRecetaError(null);
      setCodigoReceta(item.codigoReceta);
      setDescripcionReceta(item.descripcion);

      try {
        const full = await recetaService.getReceta(item.codigoReceta);
        const batchQty = Number(full.productoTerminado?.cantidad);
        if (batchQty > 0) setCantidad(batchQty);

        const pt = full.productoTerminado;
        const ptCodigo = (pt?.codigoArticulo ?? item.codigoProductoTerminado ?? '').trim();
        if (!ptCodigo) {
          setPtError('La receta no tiene producto terminado');
          enqueueSnackbar('La receta no tiene producto terminado', { variant: 'warning' });
          return;
        }
        setCodigoArticulo(ptCodigo);
        setDescripcion((pt?.descripcion ?? item.descripcionProductoTerminado ?? '').trim());
        setPtError(null);
      } catch {
        const ptCodigo = (item.codigoProductoTerminado ?? '').trim();
        if (!ptCodigo) {
          setPtError('La receta no tiene producto terminado');
          enqueueSnackbar('La receta no tiene producto terminado', { variant: 'warning' });
          return;
        }
        setCodigoArticulo(ptCodigo);
        setDescripcion((item.descripcionProductoTerminado ?? '').trim());
        setPtError(null);
      }
    },
    [enqueueSnackbar],
  );

  const resolveRecetaExact = useCallback(
    async (codigo: string) => {
      const trimmed = codigo.trim();
      if (!trimmed || !isNew) return;
      try {
        const receta = await recetaService.getReceta(trimmed);
        await aplicarRecetaSeleccionada({
          codigoReceta: receta.codigoReceta,
          descripcion: receta.descripcion,
          codigoProductoTerminado: receta.productoTerminado?.codigoArticulo ?? null,
          descripcionProductoTerminado: receta.productoTerminado?.descripcion ?? null,
          cantidadComponentes: receta.componentes?.length ?? 0,
        });
      } catch {
        setRecetaError('Receta no encontrada');
        setDescripcionReceta('');
        setRecetaDialogOpen(true);
      }
    },
    [isNew, aplicarRecetaSeleccionada],
  );

  const handleSave = async () => {
    if (!codigoArticulo.trim()) {
      enqueueSnackbar('Seleccioná el producto terminado', { variant: 'warning' });
      return;
    }
    if (depositoOrigen === '' || depositoDestino === '') {
      enqueueSnackbar('Seleccioná depósito origen y destino', { variant: 'warning' });
      return;
    }
    try {
      setSaving(true);
      const payload = {
        cantidad: Number(cantidad) || 0,
        codigoMaquina: codigoMaquina === '' ? null : Number(codigoMaquina),
        codigoMolde: codigoMolde === '' ? null : Number(codigoMolde),
        depositoOrigen: Number(depositoOrigen),
        depositoDestino: Number(depositoDestino),
        observaciones: observaciones.trim() || undefined,
      };
      if (isNew) {
        await produccionService.create({ codigoArticulo: codigoArticulo.trim(), ...payload });
      } else {
        await produccionService.update(Number(id), payload);
      }
      enqueueSnackbar('Orden guardada', { variant: 'success' });
      navigate('/produccion/ordenes');
    } catch (error: unknown) {
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Error al guardar la orden';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const ptResuelto = Boolean(descripcion.trim());
  const recetaResuelta = Boolean(descripcionReceta.trim() || (!isNew && codigoReceta.trim()));

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Button startIcon={<ArrowBack />} onClick={() => navigate('/produccion/ordenes')} sx={{ textTransform: 'none' }}>
              Órdenes de producción
            </Button>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {isNew ? 'Nueva orden de producción' : `Orden #${id}`}
            </Typography>
          </Stack>

          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
              <Box sx={{ flex: 1, width: '100%' }}>
                <RecetaArticuloCodigoField
                  label="Código artículo (PT/SE)"
                  size="medium"
                  value={codigoArticulo}
                  articuloResuelto={ptResuelto}
                  resolving={resolvingPt}
                  disabled={!isNew}
                  error={Boolean(ptError)}
                  helperText={ptError || ' '}
                  tiposArticulo="PT,SE"
                  onChange={(raw) => {
                    setPtError(null);
                    setCodigoArticulo(raw);
                    if (descripcion) {
                      setDescripcion('');
                      setCodigoReceta('');
                      setDescripcionReceta('');
                      setRecetaError(null);
                    }
                  }}
                  onSelect={(item) => void aplicarProductoTerminado(item)}
                  onResolveExact={(codigo) => void resolveProductoExact(codigo)}
                  onOpenDialog={() => setPtDialogOpen(true)}
                  sx={{ m: 0, '& input': { fontFamily: 'monospace' } }}
                />
              </Box>
              <TextField
                label="Descripción"
                value={descripcion}
                fullWidth
                disabled
                helperText=" "
                sx={{ flex: 1 }}
              />
              <Box sx={{ flex: 1, width: '100%' }}>
                <RecetaCodigoLookupField
                  value={codigoReceta}
                  descripcionHint={descripcionReceta || undefined}
                  recetaResuelta={recetaResuelta}
                  disabled={!isNew}
                  error={Boolean(recetaError)}
                  helperText={recetaError || ' '}
                  onChange={(raw) => {
                    setRecetaError(null);
                    setCodigoReceta(raw);
                    if (descripcionReceta) setDescripcionReceta('');
                  }}
                  onSelect={(item) => void aplicarRecetaSeleccionada(item)}
                  onResolveExact={(codigo) => void resolveRecetaExact(codigo)}
                  onOpenDialog={() => setRecetaDialogOpen(true)}
                />
              </Box>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Cantidad a producir"
                type="number"
                value={cantidad}
                onChange={(e) => setCantidad(Number(e.target.value))}
                fullWidth
                helperText="Por defecto: cantidad (batch) definida en la receta"
              />
              <TextField
                select
                label="Depósito origen (MP)"
                value={depositoOrigen}
                onChange={(e) => setDepositoOrigen(Number(e.target.value))}
                fullWidth
              >
                {ubicaciones.map((u) => (
                  <MenuItem key={u.nroUbicacion} value={u.nroUbicacion}>
                    {etiquetaUbicacionSeleccion(u)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Depósito destino (PT)"
                value={depositoDestino}
                onChange={(e) => setDepositoDestino(Number(e.target.value))}
                fullWidth
              >
                {ubicaciones.map((u) => (
                  <MenuItem key={u.nroUbicacion} value={u.nroUbicacion}>
                    {etiquetaUbicacionSeleccion(u)}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label="Máquina"
                value={codigoMaquina}
                onChange={(e) => setCodigoMaquina(e.target.value === '' ? '' : Number(e.target.value))}
                fullWidth
              >
                <MenuItem value="">Sin máquina</MenuItem>
                {maquinas.map((m) => (
                  <MenuItem key={m.codigoMaquina} value={m.codigoMaquina}>
                    {m.descripcion || m.codigoMaquina}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Molde"
                value={codigoMolde}
                onChange={(e) => setCodigoMolde(e.target.value === '' ? '' : Number(e.target.value))}
                fullWidth
              >
                <MenuItem value="">Sin molde</MenuItem>
                {moldes.map((m) => (
                  <MenuItem key={m.codigoMolde} value={m.codigoMolde}>
                    {m.descripcion || m.codigoMolde}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <TextField
              label="Observaciones"
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              multiline
              minRows={2}
              fullWidth
            />

            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              {!isNew && puedeConfirmar && (
                <Button
                  variant="outlined"
                  color="success"
                  onClick={() => navigate(`/produccion/ordenes/${id}/confirmar`)}
                >
                  Confirmar producción
                </Button>
              )}
              {!isNew && (estadoOrden ?? '').toLowerCase().replace(/\s/g, '').includes('finaliz') && (
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/produccion/ordenes/${id}/desvios`)}
                >
                  Ver desvíos de consumo
                </Button>
              )}
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
                Guardar
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <RecetaArticuloSearchDialog
        open={ptDialogOpen}
        title="Buscar producto a fabricar"
        tiposArticulo="PT,SE"
        initialSearchTerm={codigoArticulo || descripcion}
        onClose={() => setPtDialogOpen(false)}
        onSelect={(item) => {
          setPtDialogOpen(false);
          void aplicarProductoTerminado(item);
        }}
      />

      <RecetaSearchDialog
        open={recetaDialogOpen}
        title="Buscar receta"
        initialSearchTerm={codigoReceta || descripcionReceta}
        onClose={() => setRecetaDialogOpen(false)}
        onSelect={(item) => {
          setRecetaDialogOpen(false);
          void aplicarRecetaSeleccionada(item);
        }}
      />
    </Box>
  );
};

export default OrdenProduccionForm;
